import { useContext, useEffect, useRef, useState } from "react";
import { Filter, ChevronDown, Check, Loader2 } from "lucide-react";
import AuthContext from "../context/AuthContext";
import { fetchMediaAssets, fetchMediaAssetsPage } from "../api/MediaAPI";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import useClickOutside from "../hooks/useClickOutside";
import MediaAssetGridItem from "../components/MediaAssetGridItem";
import { useModal } from "../components/ModalManager";

const FILTERS = [
  { value: "all", label: "All" },
  { value: "image", label: "Images" },
  { value: "video", label: "Videos" },
];

const FETCH_DELAY = 1000;

export default function MediaLibrary() {
  const { token } = useContext(AuthContext);
  const { openModal } = useModal();

  const [assets, setAssets] = useState([]);
  const [nextUrl, setNextUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [lastFetchedTime, setLastFetchTime] = useState(0);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);

  const [mediaFilter, setMediaFilter] = useState("all");
  const [filterMenuOpen, setFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef(null);
  const filterButtonRef = useRef(null);
  useClickOutside([filterMenuRef, filterButtonRef], () => setFilterMenuOpen(false));

  // Reset and reload from the first page whenever the filter changes.
  // The server-side `media_type` filter keeps cursor pagination consistent
  // (client-side filtering of a paginated list would desync page counts).
  useEffect(() => {
    if (!token) return;

    let canceled = false;
    const ac = new AbortController();

    setAssets([]);
    setNextUrl(null);
    setError("");
    setLoading(true);
    setHasFetched(false);

    (async () => {
      try {
        const data = await fetchMediaAssets(token, { mediaType: mediaFilter, signal: ac.signal });
        if (canceled) return;
        setAssets(data.results || []);
        setNextUrl(data.next);
      } catch (err) {
        if (!canceled) {
          console.error("Error loading media library", err);
          setError("Couldn't load your media. Please try again.");
        }
      } finally {
        if (!canceled) {
          setLoading(false);
          setHasFetched(true);
          setLastFetchTime(Date.now());
        }
      }
    })();

    return () => {
      canceled = true;
      ac.abort();
    };
  }, [token, mediaFilter]);

  // Load subsequent pages by following the backend's cursor `next` link.
  useEffect(() => {
    if (page === 0) return; // page 0 is handled by the filter-load effect above
    if (!nextUrl || loading) return;

    let canceled = false;
    setLoading(true);

    (async () => {
      try {
        const data = await fetchMediaAssetsPage(nextUrl, token);
        if (canceled) return;
        setAssets((prev) => [...prev, ...(data.results || [])]);
        setNextUrl(data.next);
      } catch (err) {
        if (!canceled) {
          console.error("Error loading more media", err);
          setError("Couldn't load more media. Please try again.");
        }
      } finally {
        if (!canceled) {
          setLoading(false);
          setHasFetched(true);
          setLastFetchTime(Date.now());
        }
      }
    })();

    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleLoadMore = () => {
    const now = Date.now();
    if (!nextUrl || loading || !hasFetched || now - lastFetchedTime < FETCH_DELAY) return;
    setPage((prev) => prev + 1);
  };

  const loaderRef = useInfiniteScroll(handleLoadMore);

  const selectedFilterLabel = FILTERS.find((f) => f.value === mediaFilter)?.label || "All";

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Media</h1>

        <div className="relative">
          <button
            ref={filterButtonRef}
            type="button"
            onClick={() => setFilterMenuOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={filterMenuOpen}
            className="flex items-center gap-1.5 rounded-full border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-100"
          >
            <Filter size={14} />
            <span>{selectedFilterLabel}</span>
            <ChevronDown size={14} />
          </button>

          {filterMenuOpen && (
            <div
              ref={filterMenuRef}
              role="listbox"
              className="absolute right-0 top-full mt-1 w-36 rounded-md border border-gray-200 bg-white shadow-md z-10 overflow-hidden"
            >
              {FILTERS.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  role="option"
                  aria-selected={mediaFilter === f.value}
                  onClick={() => {
                    setMediaFilter(f.value);
                    setFilterMenuOpen(false);
                  }}
                  className="flex w-full items-center justify-between px-3 py-2 text-sm text-left hover:bg-gray-50"
                >
                  {f.label}
                  {mediaFilter === f.value && <Check size={14} className="text-blue-500" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

        {!hasFetched && loading && (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 size={24} className="animate-spin" />
          </div>
        )}

        {hasFetched && assets.length === 0 && !error && (
          <div className="flex flex-col items-center justify-center py-16 text-center text-gray-400">
            <p className="text-sm font-medium">No media yet.</p>
            <p className="text-xs mt-1">Images and videos you share in Blorp will show up here.</p>
          </div>
        )}

        {assets.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-1">
            {assets.map((asset) => (
              <button
                key={asset.id}
                type="button"
                onClick={() => openModal("MediaFocusModal", { media: asset })}
                aria-label={
                  asset.media_type === "video"
                    ? `Open video ${asset.original_filename || ""}`.trim()
                    : `Open image ${asset.original_filename || ""}`.trim()
                }
                className="block w-full text-left rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 hover:opacity-90 transition-opacity cursor-pointer"
              >
                <MediaAssetGridItem asset={asset} />
              </button>
            ))}
          </div>
        )}

        {nextUrl && <div ref={loaderRef} className="h-8" />}

        {loading && hasFetched && (
          <div className="flex items-center justify-center py-4 text-gray-400">
            <Loader2 size={18} className="animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
